"use client";

import { useId } from "react";
import { cn } from "@/lib/utils";

type Props = {
  className?: string;
  showText?: boolean;
  variant?: "dark" | "light";
};

/** Danish flag mark at a fixed 37:28 ratio with the cross offset to the hoist. */
function FlagMark({
  className,
  variant,
}: {
  className?: string;
  variant: "dark" | "light";
}) {
  const rawId = useId().replace(/:/g, "");
  const clipId = `dk-flag-clip-${rawId}`;

  const isDark = variant === "dark";
  const edge = isDark ? "rgba(15, 42, 52, 0.07)" : "rgba(255, 255, 255, 0.35)";

  return (
    <svg
      viewBox="0 0 37 28"
      preserveAspectRatio="xMidYMid meet"
      className={cn("block shrink-0", className)}
      aria-hidden
    >
      <defs>
        <clipPath id={clipId}>
          <rect x="0.5" y="0.5" width="36" height="27" rx="0.5" ry="0.5" />
        </clipPath>
      </defs>

      <g clipPath={`url(#${clipId})`}>
        <rect x="0.5" y="0.5" width="36" height="27" rx="0.5" fill="#C8102E" />
        <rect x="11" y="0" width="4" height="28" fill="#FFFFFF" />
        <rect x="0" y="12" width="37" height="4" fill="#FFFFFF" />
      </g>
      <rect
        x="0.5"
        y="0.5"
        width="36"
        height="27"
        rx="0.5"
        fill="none"
        stroke={edge}
        strokeWidth="0.75"
      />
    </svg>
  );
}

export function BrandLogo({ className, showText = true, variant = "dark" }: Props) {
  const isDark = variant === "dark";

  const flagSvgClass = cn(
    "aspect-[37/28] h-[2.25rem] w-auto sm:h-[2.6rem]",
  );

  if (!showText) {
    return (
      <span
        aria-hidden
        className={cn("inline-flex items-center justify-center", className)}
      >
        <FlagMark variant={variant} className="aspect-[37/28] h-12 w-auto sm:h-[3.25rem]" />
      </span>
    );
  }

  // The wordmark is a Latin name: it keeps its order and its italic on Arabic
  // pages (flag, "Danish", "Wedding" from left to right).
  return (
    <span dir="ltr" lang="en" className={cn("inline-flex min-w-0 items-stretch gap-2 sm:gap-2.5", className)}>
      {/* The flag must stay visible at every width; it fits down to 320px. */}
      <span
        className="flex shrink-0 items-stretch self-stretch py-0"
        aria-hidden
      >
        <FlagMark variant={variant} className={flagSvgClass} />
      </span>
      <span className="flex min-w-0 flex-col justify-center">
        <span className="flex items-baseline gap-0.5 leading-none">
          <span
            className={cn(
              "font-display text-[1.28rem] font-semibold leading-none tracking-[-0.01em] sm:text-[1.56rem]",
              isDark ? "text-navy-900" : "text-cream-50",
            )}
          >
            Danish
          </span>
          <span
            className={cn(
              "font-display text-[1.28rem] font-normal italic leading-none tracking-[-0.01em] sm:text-[1.56rem]",
              // Same red as the flag, so the two reds never clash.
              isDark ? "text-coral-500" : "text-cream-50",
            )}
          >
            Wedding
          </span>
        </span>
      </span>
    </span>
  );
}
